import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import {
    Form,
    FormControl,
    FormControlStack,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS } from 'src/features/moneyAccounts/utils/constants';
import { SubscriptionItemFormSchemaValues } from 'src/features/subscriptionItems/utils/formSchema';
import { subscriptionItemsMessages } from 'src/features/subscriptionItems/utils/messages';
import { useListServicePlanItems } from 'src/features/servicePlanItems/api/useListServicePlanItems';
import { useGetSubscription } from 'src/features/subscriptions/api/useGetSubscription';
import { SubscriptionItem } from 'src/features/subscriptionItems/utils/types';
import { useListSubscriptionItems } from 'src/features/subscriptionItems/api/useListSubscriptionItems';

type SubscriptionItemProps = {
    form: UseFormReturn<SubscriptionItemFormSchemaValues>;
    onSubmit: (values: SubscriptionItemFormSchemaValues) => void;
    /** The item being edited; omitted when creating. */
    subscriptionItem?: SubscriptionItem;
};

function SubscriptionItemForm({ onSubmit, form, subscriptionItem }: SubscriptionItemProps) {
    const { formatMessage } = useIntl();
    const { subscriptionId = '' } = useParams();

    const { data: subscription } = useGetSubscription({ id: subscriptionId, config: { enabled: !!subscriptionId } });
    const { data: subscriptionItems } = useListSubscriptionItems({
        subscriptionId,
        config: { enabled: !!subscriptionId },
    });

    // Only the optional, active items of *this* subscription's own plan can be added. Querying
    // without a plan filter returned a mix of items from every plan in the system, capped at the
    // default page size, so the list was both wrong and incomplete.
    const servicePlanId = subscription?.service_plan?.id;
    const { data: servicePlanItems } = useListServicePlanItems({
        service_plan: servicePlanId ? [servicePlanId] : undefined,
        is_required: false,
        is_active: true,
        config: { enabled: !!servicePlanId },
    });

    // An item already on the subscription cannot be added twice. The edited item's own service stays
    // selectable — filtering it out left the select blank.
    const usedServicePlanItemIds = new Set(
        (subscriptionItems?.results ?? [])
            .filter((item) => item.id !== subscriptionItem?.id)
            .map((item) => item.service_plan_item?.id)
            .filter(Boolean),
    );
    const selectableServicePlanItems = (servicePlanItems?.results ?? []).filter(
        (option) => !usedServicePlanItemIds.has(option.id),
    );
    const currentServiceMissing =
        !!subscriptionItem && !selectableServicePlanItems.some((o) => o.id === subscriptionItem.service_plan_item.id);
    const serviceLocked = !!subscriptionItem?.is_billed;

    const { start_date } = form.getValues();

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="service_plan_item"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...servicePlanItemsMessages.item} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={field.onChange}
                                    value={field.value ?? ''}
                                    disabled={serviceLocked}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(servicePlanItemsMessages.item)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {currentServiceMissing && subscriptionItem && (
                                            <SelectItem value={subscriptionItem.service_plan_item.id}>
                                                {subscriptionItem.service_plan_item.name}
                                            </SelectItem>
                                        )}
                                        {selectableServicePlanItems.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {option.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            {serviceLocked && (
                                <FormDescription>
                                    {formatMessage(subscriptionItemsMessages.serviceLockedHint)}
                                </FormDescription>
                            )}
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(subscriptionsMessages.startDate)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    captionLayout="dropdown"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(subscriptionsMessages.endDate)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    captionLayout="dropdown"
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabledDateRange={{ before: new Date(start_date) }}
                                />
                            </FormControl>
                            <FormDescription>{formatMessage(subscriptionItemsMessages.endDateHint)}</FormDescription>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<SubscriptionItemFormSchemaValues>
                form={form}
                name="is_active"
                isBoolean={true}
                required
                label={formatMessage(subscriptionItemsMessages.isActive)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />
        </Form>
    );
}

export default SubscriptionItemForm;
